import { Loader2, X } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { ApiError } from "@/lib/api.ts";
import {
  useCreateCustomer,
  useUpdateCustomer,
  type CustomerCreateInput,
  type CustomerUpdateInput,
} from "@/queries/admin/customers.ts";
import { CustomerDocType, type CustomerOut } from "@/types/api.ts";

import { Field, Toggle, inputClass } from "./formHelpers.tsx";

interface Props {
  open: boolean;
  customer: CustomerOut | null;
  onClose: () => void;
  /** Si se setea, se llama tras crear (no aplica en edit) y se le pasa el
   *  cliente recién creado. Útil para el flujo del POS donde el dialog se
   *  abre desde el CustomerSelector y queremos auto-seleccionar al cliente. */
  onCreated?: (customer: CustomerOut) => void;
  /** Prefill del doc_number al abrir en modo create. Útil cuando el cajero
   *  estaba tipeando un doc en el selector y se le ofrece crear. */
  initialDocNumber?: string;
}

interface FormValues {
  doc_type: CustomerDocType;
  doc_number: string;
  full_name: string;
  address: string;
  email: string;
  phone: string;
  is_active: boolean;
}

const DOC_TYPE_LABELS: Record<CustomerDocType, string> = {
  [CustomerDocType.DNI]: "DNI",
  [CustomerDocType.RUC]: "RUC",
  [CustomerDocType.FOREIGN]: "Otro/Extranjero",
};

function defaults(): FormValues {
  return {
    doc_type: CustomerDocType.DNI,
    doc_number: "",
    full_name: "",
    address: "",
    email: "",
    phone: "",
    is_active: true,
  };
}

function fromCustomer(c: CustomerOut): FormValues {
  return {
    doc_type: c.doc_type,
    doc_number: c.doc_number,
    full_name: c.full_name,
    address: c.address ?? "",
    email: c.email ?? "",
    phone: c.phone ?? "",
    is_active: c.is_active,
  };
}

/** Reglas client-side. Backend re-valida con peru_utils (DNI checksum, RUC SUNAT). */
function validateDocNumber(docType: CustomerDocType, value: string): string | true {
  const v = value.trim();
  if (!v) return "Documento requerido";
  if (docType === CustomerDocType.DNI) {
    if (!/^\d{8}$/.test(v)) return "DNI: 8 dígitos";
  } else if (docType === CustomerDocType.RUC) {
    if (!/^\d{11}$/.test(v)) return "RUC: 11 dígitos";
  }
  return true;
}

export function CustomerFormDialog({
  open,
  customer,
  onClose,
  onCreated,
  initialDocNumber,
}: Props) {
  const isEdit = customer != null;
  const create = useCreateCustomer();
  const update = useUpdateCustomer(customer?.id ?? "");

  /** Si initialDocNumber parece RUC (11 dígitos numéricos), pre-elige RUC; si
   *  son 8 dígitos, DNI; si no, foreign. */
  const guessDocType = (doc: string | undefined): CustomerDocType => {
    if (!doc) return CustomerDocType.DNI;
    if (/^\d{11}$/.test(doc)) return CustomerDocType.RUC;
    if (/^\d{8}$/.test(doc)) return CustomerDocType.DNI;
    return CustomerDocType.FOREIGN;
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: defaults() });

  useEffect(() => {
    if (open) {
      if (customer) {
        reset(fromCustomer(customer));
      } else {
        const seed = initialDocNumber?.trim() ?? "";
        reset({
          ...defaults(),
          doc_type: guessDocType(seed),
          doc_number: seed,
        });
      }
      create.reset();
      update.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customer, initialDocNumber]);

  const docType = watch("doc_type");

  const onSubmit = async (values: FormValues) => {
    try {
      if (isEdit) {
        const body: CustomerUpdateInput = {
          full_name: values.full_name.trim(),
          address: values.address.trim() || null,
          email: values.email.trim() || null,
          phone: values.phone.trim() || null,
          is_active: values.is_active,
        };
        await update.mutateAsync(body);
      } else {
        const body: CustomerCreateInput = {
          doc_type: values.doc_type,
          doc_number: values.doc_number.trim(),
          full_name: values.full_name.trim(),
          address: values.address.trim() || null,
          email: values.email.trim() || null,
          phone: values.phone.trim() || null,
          is_active: values.is_active,
        };
        const created = await create.mutateAsync(body);
        onCreated?.(created);
      }
      onClose();
    } catch {
      /* error abajo */
    }
  };

  const mutation = isEdit ? update : create;
  const errorMsg =
    mutation.error instanceof ApiError ? mutation.error.message : mutation.error?.message;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
      <div className="max-h-[95vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-slate-900">
            {isEdit ? "Editar cliente" : "Nuevo cliente"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            aria-label="Cerrar"
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 px-6 py-5">
          <div className="grid grid-cols-3 gap-3">
            <Field
              label="Tipo de doc."
              required
              hint={isEdit ? "No editable." : undefined}
              className="col-span-1"
            >
              <select
                {...register("doc_type")}
                disabled={isEdit}
                className={`${inputClass} disabled:bg-slate-100 disabled:text-slate-500`}
              >
                {Object.values(CustomerDocType).map((t) => (
                  <option key={t} value={t}>
                    {DOC_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Número"
              error={errors.doc_number?.message}
              required
              hint={isEdit ? "No editable." : undefined}
              className="col-span-2"
            >
              <input
                {...register("doc_number", {
                  validate: (v) => (isEdit ? true : validateDocNumber(docType, v)),
                })}
                disabled={isEdit}
                autoFocus={!isEdit}
                inputMode={
                  docType === CustomerDocType.DNI || docType === CustomerDocType.RUC
                    ? "numeric"
                    : "text"
                }
                className={`${inputClass} font-mono disabled:bg-slate-100 disabled:text-slate-500`}
                placeholder={
                  docType === CustomerDocType.DNI
                    ? "12345678"
                    : docType === CustomerDocType.RUC
                      ? "20123456789"
                      : ""
                }
              />
            </Field>
          </div>

          <Field label="Nombre / Razón social" error={errors.full_name?.message} required>
            <input
              {...register("full_name", {
                required: "Nombre requerido",
                maxLength: { value: 255, message: "Máx 255" },
              })}
              autoFocus={isEdit}
              className={inputClass}
              placeholder={docType === CustomerDocType.RUC ? "Empresa SAC" : "Apellidos, Nombres"}
            />
          </Field>

          <Field label="Dirección">
            <input {...register("address")} className={inputClass} placeholder="Av. ..." />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Email" error={errors.email?.message}>
              <input
                type="email"
                {...register("email", {
                  maxLength: { value: 255, message: "Máx 255" },
                })}
                className={inputClass}
                placeholder="cliente@..."
              />
            </Field>
            <Field label="Teléfono" error={errors.phone?.message}>
              <input
                {...register("phone", {
                  maxLength: { value: 30, message: "Máx 30" },
                })}
                className={inputClass}
                placeholder="999 ..."
              />
            </Field>
          </div>

          <Toggle label="Activo" {...register("is_active")} />

          {errorMsg && (
            <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {errorMsg}
            </p>
          )}

          <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={mutation.isPending}
              className="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex items-center gap-2 rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:bg-slate-300"
            >
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? "Guardar" : "Crear"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
